import type { OutcomeLog, OutcomeMetric, ProfileProject, ProjectAction } from '@photofeeler/shared';

export interface ProjectReturnPrompt {
  id: string;
  projectId: string;
  projectTitle: string;
  title: string;
  detail: string;
  metric: OutcomeMetric;
  ctaLabel: string;
  source: 'audit' | 'rewrite';
  timestamp: string;
}

function latestAiRewriteAt(project: ProfileProject): string | null {
  const timestamps = project.profileWorkspace.sections
    .flatMap(section => section.draftVersions)
    .filter(version => version.source === 'ai_rewrite')
    .map(version => version.createdAt)
    .filter(Boolean);

  return timestamps.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null;
}

function latestOutcomeAt(projectId: string, outcomeLogs: OutcomeLog[]): string | null {
  const timestamps = outcomeLogs
    .filter(log => log.projectId === projectId)
    .map(log => log.loggedAt)
    .filter(Boolean);

  return timestamps.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null;
}

export function getPrimaryFocusAction(project: ProfileProject): ProjectAction | null {
  return project.nextActions.find(action => !action.completed) || null;
}

export function buildReturnPrompts(
  projects: ProfileProject[],
  outcomeLogs: OutcomeLog[]
): ProjectReturnPrompt[] {
  const prompts: Array<ProjectReturnPrompt | null> = projects
    .map(project => {
      const auditAt = project.profileWorkspace.audit?.analyzedAt || null;
      const rewriteAt = latestAiRewriteAt(project);
      const latestRelevant = [auditAt, rewriteAt]
        .filter((value): value is string => Boolean(value))
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

      if (!latestRelevant) return null;

      const latestOutcome = latestOutcomeAt(project.id, outcomeLogs);
      if (latestOutcome && new Date(latestOutcome).getTime() >= new Date(latestRelevant).getTime()) {
        return null;
      }

      const source = rewriteAt && rewriteAt === latestRelevant ? 'rewrite' : 'audit';
      const metric: OutcomeMetric = project.vertical === 'linkedin' ? 'recruiter_messages' : 'matches';
      const metricLabel = project.vertical === 'linkedin' ? 'recruiter messages' : 'matches';

      return {
        id: `${project.id}-${source}-${latestRelevant}`,
        projectId: project.id,
        projectTitle: project.title,
        title:
          source === 'rewrite'
            ? `${project.title} follow-up`
            : `${project.title} follow-up`,
        detail:
          source === 'rewrite'
            ? `You updated this workspace recently. Give the new profile a little time, then log ${metricLabel}.`
            : `You ran a full audit recently. Try the recommended changes, then log ${metricLabel}.`,
        metric,
        ctaLabel: `Log ${metricLabel}`,
        source,
        timestamp: latestRelevant,
      };
    });

  return prompts
    .filter((prompt): prompt is ProjectReturnPrompt => prompt !== null)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}
